import React, { useEffect, useState } from 'react';
import { Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  onInstallAvailabilityChange,
  triggerInstallPrompt,
  isStandaloneDisplay,
  isIOS,
} from '../lib/pwa';

/**
 * InstallButton
 * - Hidden while running in standalone (already installed) or when no prompt is available
 *   AND the device isn't iOS.
 * - On iOS Safari (no beforeinstallprompt), shows a help dialog with Add to Home Screen steps.
 */
export function InstallButton({ variant = 'default', size = 'default', label = 'Install App', className = '', testId = 'install-pwa-btn' }) {
  const [prompt, setPrompt] = useState(null);
  const [standalone, setStandalone] = useState(isStandaloneDisplay());
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const ios = isIOS();

  useEffect(() => {
    setStandalone(isStandaloneDisplay());
    return onInstallAvailabilityChange(setPrompt);
  }, []);

  if (standalone) return null;
  if (!prompt && !ios) return null;

  const handleClick = async () => {
    if (prompt) {
      await triggerInstallPrompt();
    } else if (ios) {
      setShowIOSHelp(true);
    }
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant={variant}
        size={size}
        className={className}
        data-testid={testId}
      >
        <Download className="w-4 h-4 mr-2" />
        {label}
      </Button>

      <Dialog open={showIOSHelp} onOpenChange={setShowIOSHelp}>
        <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2]" data-testid="ios-install-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#1ABC9C]" />
              Install Elara WFM on iPhone
            </DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              iOS Safari doesn't offer a one-click install, but adding Elara to the Home Screen takes three taps.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-3 text-sm text-[#F2F2F2]">
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#1ABC9C] text-[#1A1A1A] font-semibold flex items-center justify-center shrink-0">1</span>
              <span>Tap the <strong>Share</strong> icon in the Safari toolbar (the square with an arrow pointing up).</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#1ABC9C] text-[#1A1A1A] font-semibold flex items-center justify-center shrink-0">2</span>
              <span>Scroll down and tap <strong>Add to Home Screen</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 rounded-full bg-[#1ABC9C] text-[#1A1A1A] font-semibold flex items-center justify-center shrink-0">3</span>
              <span>Tap <strong>Add</strong> in the top-right corner. Elara will appear on your Home Screen as a full-screen app.</span>
            </li>
          </ol>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InstallButton;
